import {
    A9Component,
    AngularInjector,
    CollectionResource,
    type IActiveSorting,
    type IBaseDataProvider,
    type ICollectionParams,
    type IPagination,
    type IResourceParams,
    type PartialType
} from '../../system';
import { typedFastCopy } from '../../utils';

import { AbstractSelection, ComponentController, EventEmitter } from '../../system/classes';
import { type IGridListingFilter } from './grid.listing.filter';
import { type IGridStrategy } from './strategies';
import { type IListingGridColumn } from '../../listing/IListingGridColumn';
import { v1 as uuidv1 } from 'uuid';
import { GridDragManager } from '../../drag-manager/GridDragManager';
import { type IGrid } from './IGrid';
import { GridMarker } from '../../grid/marker';
import { type IPaginationApi } from '../../pagination';
import template from './grid.component.html';

import { ArraySelection, SingleSelection } from '../../listing/listing.selection';
import { type ISelectionStrategy } from './strategies/abstract.selection.strategy';
import { A9Grid, type IA9GridOptions } from '../../grid/a9-grid/a9-grid.component';
import { type IFilterStrategy } from './strategies/abstract.filter.strategy';
import { createCollectionResource } from '../../system/data-provider/utils';

export type ListingGridV1TState = {}; // TODO: need implement

// @toDo: escape title, disabled and display
export type GridAction<Row = any> = {
    // button with dropdown
    title?: string | ((row: Row) => string);
    id?: string;
    disabled?: (row: Row, ...params) => boolean;
    display?: (row: Row, ...params) => boolean;
    // icons buttons
    name?: string;
    class?: string | ((row: Row) => string);
    template?: string;
    active?: (row: Row, ...params) => boolean;
    busy?: (row: Row, ...params) => boolean;
    // for all
    action: (row: Row, ...params) => any;
    isAccessible?: (row: Row, ...params) => boolean;
};

export enum GridComponentEvents {
    LoadingStared = 'listing::started',
    Loaded = 'listing::loaded',
    LoadingFailed = 'listing::failed',
    Destroyed = 'listing::destroyed',
    RowsRendered = 'listing::rows-rendered',
    RowExpanded = 'listing::expanded',
    RowCollapsed = 'listing::collapsed'
}

interface IDetachable {
    detach(): void;
}

@A9Component({
    selector: 'listing-grid-v1',
    scope: {
        state: '=?',
        appScope: '=?',
        options: '=?',
        onReady: '=?'
    },
    replace: true,
    template() {
        return template;
    }
})
export class GridComponent extends ComponentController implements IGrid {
    private dead: boolean = false;
    protected id: string;
    protected simpleGridResolve: (value: A9Grid) => void;
    public simpleGridReady = new Promise<A9Grid>(resolve => {
        this.simpleGridResolve = resolve;
    });
    private strategy: IGridStrategy;
    private fetchAllItems = false;

    private dataResource: CollectionResource<any>;
    private requestParams: ICollectionParams = {
        filters: {},
        pagination: {
            number: 0,
            size: 0
        }
    };
    private sortings: IActiveSorting[] = [];
    private filterObjects: IGridListingFilter[] = [];
    private dragManager: GridDragManager = null;
    private dataProvider: IBaseDataProvider;
    private loadingErrorHandler: (error) => void = null;
    private dataHandler: (data: any[], page: IPagination) => any[] = null;
    private active = false;
    private markerGrid: GridMarker;
    private markerUnWatch;
    private paginationApi: IPaginationApi;
    private displayProgressBar: boolean = true;
    private defaultSortings: IActiveSorting[] = [];
    private selectionStrategy: ISelectionStrategy;
    private gridApi: any; // UIGridApi
    private filterStrategy: IFilterStrategy = null;
    private detachables: IDetachable[] = [];
    private rawData: any;

    constructor(
        protected $scope: any,
        protected $element,
        protected $timeout,
        protected dialogManager
    ) {
        super($scope);
        this.id = `id${uuidv1()}`;
        this.setState(this.$scope.state);
        this.run();
        $scope.$on('$destroy', () => this.doDetach());
    }

    public getId() {
        return this.id;
    }

    public activate() {
        this.active = true;
    }

    public deactivate() {
        this.active = false;
    }

    public setSelectionStrategy(strategy: ISelectionStrategy) {
        this.selectionStrategy = strategy;
    }

    public setFilterStrategy(strategy: IFilterStrategy) {
        this.filterStrategy = strategy;
    }

    public filterStrategyUpdate() {
        return this.filterStrategy.update(this.filterObjects);
    }

    public getFilterStrategy() {
        return this.filterStrategy;
    }

    //todo docs
    public setInnerHeight(height) {
        if ((height > 0 && height < 320032) || height === '100%') {
            this.$element.children('*').eq(0).height(height);
        }
    }

    //todo docs
    public getInnerHeight() {
        return this.$element.children('*').eq(0).height();
    }

    /**
     * Please set draggable option before init grid
     * @param dragManager
     */
    public setDragManager(dragManager: GridDragManager = null) {
        if (this.dragManager) {
            this.dragManager.destroy();
        }
        this.dragManager = dragManager || new GridDragManager(this.id);
        this.initDragManager();
    }

    public switchDragManager(dragManager: GridDragManager = null) {
        if (!this.dragManager) {
            this.setDragManager(dragManager);
        }
        this.dragManager = dragManager || new GridDragManager(this.id);
        this.initDragManager();
    }

    private initDragManager() {
        if (!this.dragManager) {
            return;
        }
        this.$element.addClass('a9-ui-grid-draggable');
        this.dragManager.setScope(this.$scope);
        if (!this.hasOption('draggable')) {
            this.setOptions({ draggable: true });
        }
        this.simpleGridReady.then((api: any) => {
            this.dragManager.setGridApi(api.getGridApi());
        });
    }

    public getDragManager() {
        return this.dragManager;
    }

    public getMaxHeight() {
        return this.strategy.getInnerGridHeight();
    }

    public setStrategy(strategy: IGridStrategy) {
        if (this.strategy) {
            this.detachDetachable(this.strategy);
        }

        this.strategy = strategy;
        this.fetchAllItems = this.strategy.usesAllItems();
        this.strategy.setGridElement(this.$element[0]);
        this.registerDetachable(this.strategy);

        let pageAmount = (this.dragManager && this.strategy.getGridPageAmount() - 1) || this.strategy.getGridPageAmount();
        if (pageAmount < 1) {
            pageAmount = 1;
        }
        this.requestParams.pagination.size = pageAmount || 1;
        this.updatePagination();
        this.strategy.on('gridStrategy::changeHeight', () => {
            this.$scope.stretchToHeight = this.strategy.isStretchable() ? this.strategy.getInnerGridHeight() : 0;
            if (this.requestParams.pagination.size != this.strategy.getGridPageAmount() && this.strategy.getGridPageAmount() > -1) {
                this.requestParams.pagination.size = this.strategy.getGridPageAmount();
                this.updatePagination();
                this.loadDataResource();
            }
        });
        this.$scope.showPagination = this.strategy.showPagination();
        this.$scope.gridClass = this.strategy.getCssClasses() + ' ' + ((this.$scope.options && this.$scope.options.gridClass) || '');
        const a9GridConstants = <any>AngularInjector.get('a9GridConstants');
        this.$scope.stretchToHeight = this.strategy.isStretchable() ? this.strategy.getInnerGridHeight() + a9GridConstants.headerRowHeight : 0;
    }

    public setColumns(columns: IListingGridColumn[]) {
        this.$scope.grid.columns = columns.map(column => {
            const item = { ...column }; // don't use fastCopy
            if (!item.name) {
                item.name = column.field;
            }
            return item;
        });
        this.$scope.grid.columnsReady = true;
    }

    /**
     * Recreates the underlying A9 grid with a new column configuration.
     * A9Grid reads its columns when it is compiled, so changing the column
     * array alone is not enough after the initial render.
     */
    public rebuildColumns(columns: IListingGridColumn[]): void {
        this.$scope.grid.columnsReady = false;
        this.$timeout(() => this.setColumns(columns));
    }

    /**
     * Sets data from API call for the grid
     *
     * @fires GridComponentEvents.DataLoaded
     */
    public setData(data: any[]) {
        this.rawData = data;
        this.$scope.grid.items = (this.dataHandler && this.dataHandler(data, this.$scope.grid.pagination)) || data;

        if (!this.$scope.grid || !this.$scope.grid.options || !this.$scope.grid.options.dontStopLoading) {
            this.loading(false);
        }
    }

    /**
     * Returns all data;
     */
    public getRawData() {
        return this.rawData;
    }

    public getItems() {
        return this.$scope.grid.items;
    }

    public setDataResource(
        dataProvider: IBaseDataProvider,
        resourceParams: IResourceParams = {},
        collectionParamsTransformer: (paramsIn: ICollectionParams) => ICollectionParams | null = null
    ) {
        this.dataProvider = dataProvider;
        this.dataResource && this.dataResource.close();
        this.dataResource = createCollectionResource(
            this.$scope,
            this.dataProvider,
            resourceParams,
            this.requestParams,
            collectionParamsTransformer,
            () => {
                this.loading(true);
                this.emit(GridComponentEvents.LoadingStared);
            },
            null,
            items => {
                this.setData(items);
                this.$scope.grid.sortingEventEmitter.emit('restore', this.sortings); // restore sorting
                if (!this.$scope.grid || !this.$scope.grid.options || !this.$scope.grid.options.dontStopLoading) {
                    this.loading(false);
                }

                this.$scope.$applyAsync(() => {
                    this.updatePagination();
                    this.emit(GridComponentEvents.Loaded, items);

                    if (this.selectionStrategy) {
                        this.selectionStrategy.update(items);
                    }
                });
            },
            msg => {
                this.setData([]);
                this.onLoadFail(msg);
                this.loading(false);
                this.$scope.$applyAsync(() => {
                    this.emit(GridComponentEvents.LoadingFailed, msg);
                });
            },
            this.fetchAllItems
        );
    }

    public setDataHandler(handler: (data: any[]) => any[]) {
        this.dataHandler = handler;
    }

    public getDataProvider() {
        return this.dataProvider;
    }

    /**
     * Loads the table data (fetch data from the server)
     * @param debounce if the data should be reloaded with debounce
     */
    public loadDataResource(debounce: boolean = false) {
        if (this.dataResource && this.active) {
            this.dataResource.reload(debounce);
        } else {
            this.emit(GridComponentEvents.LoadingStared);
        }
    }

    public goToPage(page: number) {
        this.setPage(page);
        this.loadDataResource();
    }

    public setPage(page: number) {
        this.requestParams.pagination.number = this.$scope.state.pagination.number = page;
    }

    public setName(name = '') {
        this.$scope.grid.name = name;
    }

    public getName() {
        return this.$scope.grid.name;
    }

    public setTotalName(name = '') {
        this.$scope.grid.totalName = name;
    }

    public getTotalName() {
        return this.$scope.grid.totalName;
    }

    public setSelection(selection) {
        this.$scope.grid.selection = selection;
        if (selection && this.isBackendSelection(selection)) {
            selection.on('globalStart', () => {
                this.loading(true);
            });
            selection.on('globalFinish', () => {
                this.loadDataResource();
            });
        }
    }

    public getRequestParams(): ICollectionParams {
        return typedFastCopy(this.requestParams);
    }

    //todo add to doc
    public getSelection(): AbstractSelection<any> {
        return this.$scope.grid.selection;
    }

    public setDefaultSortings(sortings: IActiveSorting[] = []) {
        this.defaultSortings = sortings;
        this.defaultSortings.forEach(sort => {
            sort.priority += 1000;
        });
        this.setSortings(this.sortings);
    }

    public async setSortings(sortings: IActiveSorting[] = []) {
        this.sortings = sortings.filter(sort => {
            sort.field = sort.sortField || sort.field;
            return sort.priority < 1000;
        });
        const defaultSortings = this.defaultSortings.filter(sort => !this.sortings.find(i => i.field === sort.field));
        const allSortings = [].concat(this.sortings, defaultSortings);
        if (this.sortings.length > 0) {
            this.requestParams.sorting = typedFastCopy(allSortings);
            this.$scope.grid.activeSortings = this.sortings.filter(item => !item.hidden);
        } else {
            this.sortings = [];
            this.$scope.grid.activeSortings = [];
            if (defaultSortings) {
                this.requestParams.sorting = typedFastCopy(defaultSortings);
            } else {
                delete this.requestParams.sorting;
            }
        }
        if (this.$scope.state.sortings) {
            this.$scope.state.sortings.splice(0, this.$scope.state.sortings.length);
            allSortings.forEach(sorting => {
                this.$scope.state.sortings.push(sorting);
            });
        }
        let resolve = null;
        const p = new Promise(res => (resolve = res));
        this.$scope.$applyAsync(() => {
            return resolve(this);
        });
        return p;
    }

    public getActiveSortings() {
        return typedFastCopy(this.requestParams.sorting);
    }

    public getActiveFilters() {
        return typedFastCopy(this.requestParams.filters);
    }

    public updateFilters(...filterNames: string[]) {
        if (this.dead) {
            return;
        }
        this.updateSelection();
        this.filterObjects.forEach(fo => {
            if (filterNames.length > 0) {
                if (filterNames.indexOf(fo.name()) > -1) {
                    this.updateFilter(fo);
                }
            } else {
                this.updateFilter(fo);
            }
        });
    }

    public updateSelection() {
        this.requestParams.filters = this.requestParams.filters || {};
        if (this.$scope.grid.selection && this.isBackendSelection(this.$scope.grid.selection) && this.$scope.grid.selection.id) {
            this.requestParams.filters.selectionId = this.$scope.grid.selection.id;
        } else if (this.requestParams.filters.selectionId) {
            delete this.requestParams.filters.selectionId;
        }
    }

    public updateFilter(fo: IGridListingFilter) {
        if (fo.isEmpty()) {
            fo.values().forEach(filter => {
                this.removeFilter(filter.name);
            });
        } else {
            fo.values().forEach(filter => {
                if (filter.value != null) {
                    this.setFilter(filter.name, filter.value);
                } else {
                    this.removeFilter(filter.name);
                }
            });
        }
        this.updateActiveFilters(fo);
    }

    public setFilter(filterName: string, filterValue: number | string | boolean | Array<number | string | boolean> = null) {
        if (filterValue !== null || filterValue !== undefined) {
            this.requestParams.filters = this.requestParams.filters || {};
            this.requestParams.filters[filterName] = filterValue;
        } else {
            this.removeFilter(filterName);
        }
    }

    public setFilters(filters: object) {
        for (const key in filters) {
            if (Object.prototype.hasOwnProperty.call(filters, key)) {
                this.setFilter(key, filters[key]);
            }
        }
    }

    public setFilterObject(filterObject: IGridListingFilter) {
        if (!this.filterObjects.find(fo => fo.name() === filterObject.name())) {
            this.filterObjects.push(filterObject);
        }
        this.updateFilter(filterObject);
    }

    public removeFilter(filterName: string) {
        if (this.requestParams.filters && Object.prototype.hasOwnProperty.call(this.requestParams.filters, filterName)) {
            delete this.requestParams.filters[filterName];
        }
    }

    public hideBottomFilters(flag: boolean = true) {
        this.filterObjects.forEach(filter => {
            filter.setNoBottom(flag);
            this.updateFilter(filter);
        });
    }

    public hideBottomSortings(flag: boolean = true) {
        this.sortings.forEach(sorting => {
            sorting.hidden = flag;
        });
        this.setSortings(this.sortings);
    }

    public setActions(actions: GridAction[] | ((entity: any) => GridAction[])) {
        this.$scope.actions = actions;
    }

    public setAppScope(additionalScopeParameters = null) {
        if (additionalScopeParameters) {
            for (const key in additionalScopeParameters) {
                if (Object.prototype.hasOwnProperty.call(additionalScopeParameters, key)) {
                    this.$scope.grid[key] = additionalScopeParameters[key];
                }
            }
        }
    }

    public async setState(state = {}) {
        const sortingEventEmitter = new EventEmitter();
        sortingEventEmitter.on('sort', sortings => {
            this.setSortings(sortings).then(() => {
                this.loadDataResource();
            });
        });
        this.$scope.showFooter = true;
        this.$scope.state = state;
        this.$scope.state.pagination = this.$scope.state.pagination || {};
        this.$scope.state.sortings = this.$scope.state.sortings || [];

        this.simpleGridReady.then((api: any) => {
            api.on('tableRowsRendered', rows => {
                this.emit(GridComponentEvents.RowsRendered, rows);
            });

            if (api && api.getGridApi instanceof Function) {
                this.gridApi = api.getGridApi();
                this.on(GridComponentEvents.Loaded, () => {
                    this.gridApi.expandable && this.gridApi.expandable.collapseAllRows();
                });
                api.on('rows-rendered', () => {
                    this.emit(GridComponentEvents.RowsRendered, this.gridApi.grid.renderContainers.body.visibleRowCache);
                });

                api.on('expanded', item => {
                    this.emit(GridComponentEvents.RowExpanded, item);
                });
                api.on('collapsed', item => {
                    this.emit(GridComponentEvents.RowCollapsed, item);
                });
            }
        });

        this.$scope.grid = this.$scope.grid || {
            items: [],
            columns: [],
            sortingEventEmitter: sortingEventEmitter,
            loading: true,
            columnsReady: false,
            selection: null,
            name: '',
            totalName: '',
            updatePage: this.updatePage.bind(this),
            activeFilters: {},
            activeFiltersDescriptions: {},
            deleteActiveFilter: this.deleteActiveFilter.bind(this),
            activeSortings: [],
            changeActiveSortings: this.changeActiveSortings.bind(this),
            options: this.$scope.options || {},
            pagination: this.$scope.state.pagination || {},
            filters: this.requestParams.filters
        };
        this.$scope.grid.id = this.id;
        this.$scope.onSimpleGridReady = (api: A9Grid) => {
            if (!this.dead) {
                this.simpleGridResolve(api);
                this.dragManager && this.dragManager?.onGridReady();
            }
        };
        this.$scope.onPaginationReady = (api: IPaginationApi) => {
            this.paginationApi = api;
        };
        this.setAppScope(this.$scope.appScope);
        this.updatePagination();
        return this.setSortings(this.$scope.state.sortings);
    }

    // todo doc
    public setOptions(options: PartialType<IA9GridOptions> = {}) {
        Object.assign(this.$scope.grid.options, options);
        this.$scope.showFooter = this.$scope.grid.options.showFooter === undefined || this.$scope.grid.options.showFooter;
    }

    public hasOption(name: string) {
        return this.$scope.grid.options && Object.prototype.hasOwnProperty.call(this.$scope.grid.options, name);
    }

    // todo doc
    public loading(value: boolean) {
        this.$scope.grid.loading = this.displayProgressBar && value;
        this.$scope.$applyAsync();
    }

    // todo doc
    public setLoadingErrorHandler(callback) {
        this.loadingErrorHandler = callback;
    }

    // todo doc
    public setExpandableRow(template, rowHeightHandler?: (item: any, gridWidth?: number) => number) {
        this.$scope.grid.expandableRow = template;
        if (rowHeightHandler) {
            this.on(GridComponentEvents.RowsRendered, rows => {
                rows.forEach(row => {
                    row.expandedInnerHeight = rowHeightHandler(row.entity, this.gridApi.grid.gridWidth);
                });
            });
        }
    }

    public markerToggleSelection() {
        const selection = this.$scope.grid.selection;
        if (selection) {
            const markedRow = this.markerGrid.getMarkedRow();
            selection.invert([markedRow.entity]);
        }
    }

    public markerGoNext() {
        this.markerGrid.next();
    }

    public markerGoPrev() {
        this.markerGrid.prev();
    }

    public markerGetRow() {
        const markedRow = this.markerGrid.getMarkedRow();
        return markedRow.entity;
    }

    public markerReset() {
        this.markerGrid.reset();
    }

    public markerActivate() {
        this.simpleGridReady.then(api => {
            if (!this.markerGrid) {
                this.markerGrid = new GridMarker(api.getGridApi());
                this.markerUnWatch = api.on('new-grid', gridApi => {
                    this.markerGrid.updateGridApi(gridApi);
                });
                this.markerGrid.activate();
            }
        });
    }

    public isMarkerActive() {
        return !!this.markerGrid;
    }

    public markerDeactivate() {
        if (!this.isMarkerActive()) {
            return;
        }

        //wait jquery
        this.$timeout(() => {
            if (this.isMarkerActive()) {
                this.markerGrid.deactivate();
                this.markerUnWatch();
                this.markerGrid = null;
            }
        }, 100);
    }

    public hideProgressBar() {
        this.displayProgressBar = false;
    }

    private changeActiveSortings(sortings) {
        this.setSortings(sortings).then(() => {
            this.loadDataResource();
        });
    }

    private deleteActiveFilter(filterName: string | string[]) {
        if (!(filterName instanceof Array)) {
            filterName = [<string>filterName];
        }
        const promises = [];
        let clearSelection = true;
        this.filterObjects.forEach(fo => {
            if (filterName.indexOf(fo.name()) > -1) {
                if (!fo.getClearSelection()) {
                    clearSelection = false;
                }
                fo.values().forEach(filter => {
                    this.removeFilter(filter.name);
                });
                fo.clear();
                if (fo.getStrictClearSelection()) {
                    fo.setBanClearSelection(false);
                }
                promises.push(this.updateActiveFilters(fo));
            }
        });
        if (clearSelection && this.filterStrategy) {
            promises.push(this.filterStrategyUpdate());
        }
        Promise.all(promises).then(() => {
            this.updateFilters();
            this.loadDataResource();
        });
    }

    private updatePagination() {
        this.requestParams.pagination.number = this.$scope.state.pagination.number =
            this.$scope.state.pagination.number || this.requestParams.pagination.number;
        this.$scope.state.pagination.size = this.requestParams.pagination.size;
        this.$scope.state.pagination.totalElements = this.requestParams.pagination.totalElements;
        this.$scope.state.pagination.totalPages = this.requestParams.pagination.totalPages;
    }

    public getPagination() {
        return typedFastCopy(this.$scope.state.pagination);
    }

    public getPaginationApi(): IPaginationApi {
        return this.paginationApi;
    }

    public setPaginationTitles(titles) {
        this.$scope.state.pagination.titles = titles;
    }

    public setPaginationParams(params: IPagination) {
        this.requestParams.pagination.number = params.number || 0;
        this.requestParams.pagination.size = params.size || 0;
        this.requestParams.pagination.totalElements = params.totalElements || 0;
        this.requestParams.pagination.totalPages = params.totalPages || 0;
        this.updatePagination();
    }

    private updatePage() {
        this.requestParams.pagination.number = this.$scope.state.pagination.number;
        this.loadDataResource();
    }

    private updateActiveFilters(...filterObjects: IGridListingFilter[]) {
        const promises = [];

        filterObjects.forEach(filterObject => {
            if (!filterObject.isEmpty()) {
                const promise = filterObject.ready().then(() => {
                    this.$scope.grid.activeFilters[filterObject.name()] = filterObject.values();
                    this.$scope.grid.activeFiltersDescriptions[filterObject.name()] = filterObject.descriptions();
                });
                promises.push(promise);
            } else {
                if (this.$scope.grid.activeFilters[filterObject.name()]) {
                    delete this.$scope.grid.activeFilters[filterObject.name()];
                }
                if (this.$scope.grid.activeFiltersDescriptions[filterObject.name()]) {
                    delete this.$scope.grid.activeFiltersDescriptions[filterObject.name()];
                }
            }
        });
        return Promise.all(promises);
    }

    private onLoadFail(error) {
        if (!this.dead) {
            if (this.loadingErrorHandler) {
                this.loadingErrorHandler(error);
            } else {
                this.dialogManager.error(error);
            }
        } else {
            console.error('dead GridComponent error: ', error);
        }
    }

    private isBackendSelection(selection) {
        return !(selection instanceof SingleSelection || selection instanceof ArraySelection);
    }

    // a little of more code about memory management in grid

    private detachDetachable(obj: IDetachable): void {
        const idx = this.detachables.indexOf(obj);
        if (idx !== -1) {
            const item = this.detachables.splice(idx, 1)[0];
            if (!item) {
                console.error('GridComponent::detachDetachable: detach unknown item in', this.detachables, idx, item);
            }
            if (!item.detach) {
                console.error('GridComponent::detachDetachable: cannot call detach method on', item);
            } else {
                item.detach();
            }
        }
    }

    private registerDetachable(obj: IDetachable): void {
        // TMAX: this code must be unsafe! dont add here check for detachables
        // if you have an error there, you have double grid init somewhere else, and error happened somewhere else, NOT HERE!
        // THIS LINE IS JUST AN INDICATION OF ERROR YOU HAVE
        // DO NOT CHANGE
        // DO NOT CHANGE
        // DO NOT CHANGE
        // DO NOT CHANGE
        // DO NOT CHANGE
        this.detachables.push(obj);
        // DO NOT CHANGE
        // DO NOT CHANGE
        // DO NOT CHANGE
        // DO NOT CHANGE
        // DO NOT CHANGE
    }

    private doDetach(): void {
        this.emit(GridComponentEvents.Destroyed);
        this.detachables.forEach(detachable => {
            detachable.detach();
        });
        this.detach();
    }

    private detach(): void {
        this.dead = true;
        delete this.paginationApi;
        delete this.dataProvider;
        this.filterObjects.forEach((filterObject: IDetachable) => {
            filterObject.detach();
        });
        delete this.filterObjects;

        delete this.dataResource;
        delete this.dragManager;
        delete this.loadingErrorHandler;
        delete this.dataHandler;
        delete this.gridApi;

        this.markerDeactivate();

        delete this.selectionStrategy;
        delete this.filterStrategy;
        delete this.strategy;
        delete this.detachables;

        delete this.simpleGridResolve;
        delete this.simpleGridReady;
    }
}
